<template>
  <Teleport to="body">
    <div v-if="isOpen || isClosing" class="side-drawer">
      <div
        :class="[
          'side-drawer-backdrop',
          { 'is-open': isOpen, 'is-closing': isClosing },
        ]"
        @click="handleClose"
      ></div>

      <div
        :class="[
          'side-drawer-content ',
          position,
          { 'is-open': isOpen, 'is-closing': isClosing },
        ]"
        :style="{ width: drawerWidth }"
      >
        <div class="w-full p-4 pb-1">
          <div
            v-if="breadcrumbs.length > 0"
            class="flex items-center pb-2 space-x-3 font-medium border-b"
          >
            <button
              class="grid w-8 h-8 duration-300 border border-gray-300 rounded-lg place-items-center bg-gray-50 hover:bg-gray-100"
              @click="handleClose"
            >
              <img src="@/assets/svg/circle-arrow-left.svg" class="w-6 h-6" />
            </button>

            <button
              v-for="(crumb, index) in breadcrumbs"
              :key="index"
              class="flex items-center space-x-2 hover:underline hover:disabled:no-underline"
              :disabled="index === breadcrumbs.length - 1"
              @click="crumb.action"
            >
              <span>{{ crumb.label }}</span>

              <img
                v-if="index < breadcrumbs.length - 1"
                src="@/assets/svg/chevron-right.svg"
                class="w-4 h-4"
              />
            </button>
          </div>

          <div v-else class="flex items-center pb-2 space-x-2 border-b">
            <div
              v-if="!!icon"
              class="h-10 w-10 grid place-items-center rounded-full bg-[#e8f3ea]"
            >
              <div
                class="h-[80%] w-[80%] bg-[#DFF9E6] rounded-full grid place-items-center overflow-hidden"
              >
                <figure class="h-[80%] w-[80%]">
                  <img :src="getIcon()" class="object-cover w-full h-full" />
                </figure>
              </div>
            </div>

            <h1 class="flex flex-1 text-xl font-medium line-clamp-1">
              {{ title }}
              <span v-if="flagStatusText">
                <FlagStatus
                  class="ml-1"
                  :text="flagStatusText"
                  :showText="false"
              /></span>
            </h1>

            <button
              class="grid w-10 h-10 duration-300 rounded-full place-items-center hover:bg-gray-100"
              @click="handleClose"
              aria-label="Close side drawer"
            >
              <img src="@/assets/svg/x-mark.svg" class="w-6 h-6" />
            </button>
          </div>
        </div>

        <div
          ref="scrollContainer"
          :class="[
            'side-drawer-body flex flex-col gap-5 flex-1 overflow-y-auto p-4',
            contentClass,
          ]"
        >
          <slot></slot>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
export default {
  name: "SideDrawer",
  emits: ["close", "atBottomTriggered"],
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    isChat: {
      type: Boolean,
      default: false,
    },
    position: {
      type: String,
      default: "right",
      validator: (value) => ["left", "right"].includes(value),
    },
    title: {
      type: String,
      default: "",
    },
    icon: {
      type: String,
      default: "",
    },
    contentClass: {
      type: String,
      default: "",
    },
    flagStatusText: {
      type: String,
      default: "",
    },
    drawerWidth: {
      type: String,
      default: "30%",
    },
    scrollToBottomTrigger: {
      type: Boolean,
      default: false,
    },
    breadcrumbs: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isClosing: false,
    };
  },
  methods: {
    handleClose() {
      this.isClosing = true;
      setTimeout(() => {
        this.isClosing = false;
        this.$emit("close");
      }, 600); // Match this with the animation duration
    },
    getIcon() {
      return new URL(`../../../assets/svg/${this.icon}`, import.meta.url).href;
    },
    scrollToBottom() {
      // console.log("scroll triggered");
      this.$nextTick(() => {
        const container = this.$refs.scrollContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },
    checkIfBottom() {
      const el = this.$refs.scrollContainer;
      if (!el) return;

      const threshold = 1;
      const atBottom =
        el.scrollHeight - el.scrollTop <= el.clientHeight + threshold;

      if (atBottom) {
        this.handleBottomReached();
      }
    },
    handleBottomReached() {
      if (this.isChat) {
        // const el = this.$refs.scrollContainer;
        // console.log("scroll height", el.scrollHeight);
        // console.log("scrolltop", el.scrollTop);
        // console.log("client height", el.clientHeight);
        // console.log("Bottom reached!");
        this.$emit("atBottomTriggered");
      }
      // Your logic here
    },
  },
  beforeDestroy() {
    // Clean up when component is destroyed
    if (this.$refs.scrollContainer) {
      this.$refs.scrollContainer.removeEventListener(
        "scroll",
        this.checkIfBottom
      );
    }
  },

  watch: {
    isOpen: {
      handler(newValue) {
        if (newValue) {
          this.isClosing = false;
        }
        if (newValue) {
          document.body.style.overflow = "hidden";
          document.body.style.height = "100vh";
          document.body.style.overflowY = "hidden";
        } else {
          document.body.style.overflow = "";
          document.body.style.height = "";
          document.body.style.overflowY = "";
        }
      },
      immediate: true,
    },
    scrollToBottomTrigger(oldVal, newVal) {
      if (oldVal !== newVal) {
        this.scrollToBottom();
      }
    },
    isOpen(newVal) {
      if (newVal) {
        // Wait for DOM update then check scroll position
        this.$nextTick(() => {
          this.checkIfBottom();
          // Set up scroll event listener
          if (this.$refs.scrollContainer) {
            this.$refs.scrollContainer.addEventListener(
              "scroll",
              this.checkIfBottom
            );
          }
        });
      } else {
        // Clean up when drawer closes
        if (this.$refs.scrollContainer) {
          this.$refs.scrollContainer.removeEventListener(
            "scroll",
            this.checkIfBottom
          );
        }
      }
    },
  },
};
</script>

<style scoped>
.side-drawer {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  z-index: 99;
}

.side-drawer-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(1px);
  z-index: 1;
  opacity: 0;
}

.side-drawer-backdrop.is-open {
  animation: fadeIn 0.6s forwards;
}

.side-drawer-backdrop.is-closing {
  animation: fadeOut 0.6s forwards;
}

.side-drawer-content {
  position: absolute;
  top: 0.5rem;
  display: flex;
  flex-direction: column;
  height: calc(100% - 1rem);
  z-index: 2;
  width: 30%;
  max-width: 548px;
  background-color: #fff;
  border-radius: 1rem;
}

.side-drawer-content.left {
  left: 0.5rem;
  transform: translateX(-100%);
}

.side-drawer-content.right {
  right: 0.5rem;
  transform: translateX(100%);
}

.side-drawer-content.left.is-open {
  animation: slideInLeft 0.6s forwards;
}

.side-drawer-content.right.is-open {
  animation: slideInRight 0.6s forwards;
}

.side-drawer-content.left.is-closing {
  animation: slideOutLeft 0.6s forwards;
}

.side-drawer-content.right.is-closing {
  animation: slideOutRight 0.6s forwards;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes fadeOut {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

@keyframes slideInLeft {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0);
  }
}

@keyframes slideOutLeft {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-100%);
  }
}

@keyframes slideInRight {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}

@keyframes slideOutRight {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(100%);
  }
}
</style>
